import React, { useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { setClassNames } from '@ddc/classnames-js';
import { usePrefs, useLabels } from 'wsm-common-data';
import { LoadingBox } from 'wsm-ai-loader';
import InputDisplay from './InputDisplay';
import InventoryTabs from './InventoryTabs';
import RecentSearches from './RecentSearches';
import shouldShowRecentSearches from '../util/shouldShowRecentSearches';
import hasEmptyResults from '../util/hasEmptyResults';
import hasResults from '../util/hasResults';
import useAriaLive from '../hooks/useAriaLive';
import isEnoughAddData from '../util/isEnoughAddData';
import { SearchContext, UIContext, ResultsContext } from '../contexts';
import InventoryStandard from './InventoryStandard';
import { g } from '../global-constants';

/**
 * Wraps the heading, input, and results in a `.sts-panel-content` div in
 * panel mode only (T17), so the host has one horizontal-inset property for
 * all of it instead of one per element (T26 moved the heading inside this
 * wrapper for exactly that reason). Every other mode renders its children
 * directly, with no wrapper element, so their markup stays byte-identical.
 */
const ContentWrapper = ({ isPanelMode, children }) =>
	isPanelMode ? (
		<div
			data-testid="sts-panel-content"
			className={setClassNames(['sts-panel-content'])}
		>
			{children}
		</div>
	) : (
		<>{children}</>
	);

ContentWrapper.propTypes = {
	isPanelMode: PropTypes.bool.isRequired,
	children: PropTypes.node.isRequired
};

/**
 * Presentational search results content: heading, input, recent searches,
 * loading indicator, and results. Owns no portal/backdrop/close-button/
 * focus-trap/scroll-lock -- those are the hosting wrapper's concern.
 */
const FlyOutContent = ({ closeButton }) => {
	const {
		searchTerm,
		numberOfCharactersForFirstSearchNumber,
		fetchingResults,
		isNLSFetchingResults
	} = useContext(SearchContext);

	const { doNotRenderResults } = useContext(UIContext);
	const labels = useLabels();

	const {
		recentSearches,
		pageSearchResults,
		preparedInventoryResults,
		handleLinkClick,
		addInfo,
		richContentToDisplay,
		showTabs,
		hasSingleVehicle
	} = useContext(ResultsContext);

	const { searchPlaceholderLabel, display } = usePrefs();
	const isPanelMode = display === g.NAVIGATION_DRAWER;

	const headingClassName = setClassNames(
		isPanelMode
			? ['h4', 'sts-panel-heading']
			: [
					'ddc-heading-3',
					'font-weight-bold',
					'text-capitalize',
					'float-left',
					'mt-3',
					'ddc-font-size-xlarge'
			  ]
	);

	const viewAllVehiclesLinkClassName = setClassNames(
		isPanelMode
			? ['font-weight-bold', 'py-3', 'px-2', 'ddc-font-size-small']
			: ['font-weight-bold', 'py-3', 'px-2']
	);

	const { suggestions, vehicles, allVehiclesPath } =
		preparedInventoryResults || {};

	const isSearchTermLongEnough = useMemo(
		() => searchTerm?.length >= numberOfCharactersForFirstSearchNumber,
		[searchTerm, numberOfCharactersForFirstSearchNumber]
	);

	const hasResultsLocal = useMemo(
		() => hasResults(pageSearchResults, suggestions, vehicles),
		[pageSearchResults, suggestions, vehicles]
	);

	const hasEmptyResultsLocal = useMemo(
		() => hasEmptyResults(pageSearchResults, suggestions, vehicles),
		[pageSearchResults, suggestions, vehicles]
	);

	const announceText = useAriaLive(
		isSearchTermLongEnough,
		hasResultsLocal,
		hasEmptyResultsLocal
	);

	const hasRichContentToDisplay = useMemo(
		() => isEnoughAddData(richContentToDisplay, addInfo),
		[richContentToDisplay, addInfo]
	);

	const shouldRenderNoResults = useMemo(
		() =>
			!hasResultsLocal &&
			hasEmptyResultsLocal &&
			!hasRichContentToDisplay &&
			!fetchingResults,
		[
			hasResultsLocal,
			hasEmptyResultsLocal,
			hasRichContentToDisplay,
			fetchingResults
		]
	);

	const viewLinkLabel = useMemo(
		() => labels.get('VIEW_ALL_VEHICLES_SITE_SEARCH'),
		[labels]
	);

	const handleClick = useCallback(() => {
		handleLinkClick('allInventoryLink', viewLinkLabel, '');
	}, [handleLinkClick, viewLinkLabel]);

	const showNLSLoading = useMemo(
		() => !fetchingResults && isNLSFetchingResults,
		[fetchingResults, isNLSFetchingResults]
	);

	const shouldRenderFlyoutContent =
		isSearchTermLongEnough && !doNotRenderResults;

	return (
		<>
			<div
				id="default-flyout-label"
				className={setClassNames(['sr-only'])}
				aria-live="polite"
				aria-atomic="true"
			>
				{labels.get(searchPlaceholderLabel)}
			</div>
			<div aria-live="assertive" className={setClassNames(['sr-only'])}>
				{announceText}
			</div>
			<ContentWrapper isPanelMode={isPanelMode}>
				<div
					className={setClassNames([
						'pl-0',
						'py-3',
						'pr-0',
						'clearfix'
					])}
				>
					<h2 className={headingClassName} id="search-modal-headline">
						{labels.get('SEARCH')}
					</h2>
					{closeButton}
				</div>

				<InputDisplay source="flyout" />
				<div className={setClassNames(['mt-5', 'clearfix'])}>
					{shouldShowRecentSearches(
						searchTerm,
						hasResultsLocal,
						hasEmptyResultsLocal,
						recentSearches
					) && <RecentSearches preventModalClose />}

					{showNLSLoading && (
						<LoadingBox vectorName="sparkleTwo" isGradient />
					)}

					{shouldRenderFlyoutContent && (
						<div>
							<h2
								id="results-heading"
								className={setClassNames(['sr-only'])}
							>
								{labels.get('SEARCH_RESULTS')}
							</h2>
							{showTabs ? (
								<InventoryTabs
									hasRichContentToDisplay={
										hasRichContentToDisplay
									}
									shouldRenderNoResults={
										shouldRenderNoResults
									}
								/>
							) : (
								<>
									{allVehiclesPath &&
										!shouldRenderNoResults &&
										!hasSingleVehicle && (
											<a
												onClick={handleClick}
												href={allVehiclesPath}
												className={
													viewAllVehiclesLinkClassName
												}
											>
												<span>
													{viewLinkLabel}
													<i
														className={setClassNames(
															[
																'ddc-icon',
																'ddc-icon-arrow-details',
																'ml-3'
															]
														)}
														aria-hidden="true"
													/>
												</span>
											</a>
										)}
									<div className={setClassNames(['row'])}>
										<InventoryStandard
											vehicles={vehicles}
											suggestions={suggestions}
											shouldRenderNoResults={
												shouldRenderNoResults
											}
											hasRichContentToDisplay={
												hasRichContentToDisplay
											}
										/>
									</div>
								</>
							)}
						</div>
					)}
				</div>
			</ContentWrapper>
		</>
	);
};

FlyOutContent.propTypes = {
	closeButton: PropTypes.node
};

FlyOutContent.defaultProps = {
	closeButton: null
};

export default React.memo(FlyOutContent);
